// Watchlists: every watched stock, sorted into the owner's own lists (categories), with price, day
// move, change since added, latest rating and next dated event. Lists can be made, renamed,
// recoloured and deleted, and any list can be sent to Claude for a group review. The Blacklist chip
// shows the stocks you've ruled out, with why (they still show everywhere, marked ⊘).
import { api } from "./api";
import { starsHtml } from "./assess";
import { date, esc, getMarks, pct, price, stockLink, tone } from "./format";
import { openBlacklist } from "./stockmenu";

interface List { id: number; name: string; color: string | null; count: number }
interface Watched { code: string; addedAt: string; note: string | null; priceAdded: number | null; lists: number[]; next: { date: string; title: string; kind: string } | null }
type Sort = "code" | "day" | "since" | "health" | "added" | "next";

let chosen: number | "all" | "none" | "black" = "all";
let sort: Sort = "code";
let editing: string | null = null;
const COLORS = ["a0", "a1", "a2", "a3", "a4", "a5"];

export function listReviewPrompt(name: string, codes: string[]) {
  return `Review my "${name}" watchlist in Gold: ${codes.join(", ")}. Call start_group_review from the Gold connector with group "chosen" and codes ${JSON.stringify(codes)}, and follow its guide: read everything Gold holds on each stock (including every email, upload or scraped article that mentions it), research what has changed, then save the review with save_group_review, with a verdict for every stock (buy, watch or stop watching) and any key dates you find. Then give me the short version.`;
}

// loadPrices is app.ts's cached loader for a stock's prices.
type LoadPrices = (code: string) => Promise<{ data: any }>;
let loader: LoadPrices;

export async function renderWatchlists(app: HTMLElement, query = "", loadPrices?: LoadPrices) {
  if (loadPrices) loader = loadPrices;
  const want = new URLSearchParams(query).get("edit");
  if (want) editing = want.toUpperCase();
  app.innerHTML = `<section class="watchlists"><h2>Watchlists</h2><div id="wl"><div class="loading" role="status"><span></span>Loading…</div></div></section>`;
  const el = document.getElementById("wl")!;
  let data: { lists: List[]; stocks: Watched[] };
  let ratings: Record<string, { health: number }> = {};
  try {
    const [d, att] = await Promise.all([api<typeof data>("watchlists"), api<{ ratings: typeof ratings }>("attention").catch(() => ({ ratings: {} }))]);
    data = d;
    ratings = att.ratings;
  } catch (e) {
    el.innerHTML = `<div class="notice error">${esc((e as Error).message)}</div>`;
    return;
  }
  if (typeof chosen === "number" && !data.lists.some((l) => l.id === chosen)) chosen = "all";
  const byId = new Map(data.lists.map((l) => [l.id, l]));
  const none = data.stocks.filter((s) => !s.lists.length);
  const shown = data.stocks.filter((s) => chosen === "all" || (chosen === "none" ? !s.lists.length : typeof chosen === "number" && s.lists.includes(chosen)));
  const list = typeof chosen === "number" ? byId.get(chosen)! : null;

  el.innerHTML = `
    <nav class="chips wl-lists">
      <button class="chip${chosen === "all" ? " on" : ""}" data-list="all">All <small>${data.stocks.length}</small></button>
      ${data.lists.map((l) => `<button class="chip${chosen === l.id ? " on" : ""}" data-list="${l.id}"><i class="key ${esc(l.color ?? "a0")}"></i>${esc(l.name)} <small>${l.count}</small></button>`).join("")}
      ${none.length && data.lists.length ? `<button class="chip${chosen === "none" ? " on" : ""}" data-list="none">Not in a list <small>${none.length}</small></button>` : ""}
      <button class="chip add" id="new-list">+ New list</button>
      <button class="chip black-chip${chosen === "black" ? " on" : ""}" data-list="black">⊘ Blacklist <small>${Object.keys(getMarks().blacklisted).length}</small></button>
    </nav>
    ${chosen === "black" ? blacklistHtml() : ""}
    ${list ? `<div class="card wl-list-head"><b><i class="key ${esc(list.color ?? "a0")}"></i>${esc(list.name)}</b>
      <span class="actions"><button class="small-btn" id="rename-list">Rename</button><button class="small-btn" id="colour-list">Colour</button><button class="link-btn" id="delete-list">Delete list</button></span>
      ${shown.length ? `<p class="small"><button class="primary small-btn" id="review-list">Ask Claude to review this list</button> <a class="small" target="_blank" rel="noopener" href="https://claude.ai/new?q=${encodeURIComponent(listReviewPrompt(list.name, shown.map((s) => s.code)))}">Open in claude.ai</a></p>` : ""}</div>` : ""}
    <div class="wl-tools"${chosen === "black" ? " hidden" : ""}><label class="small">Sort <select id="wl-sort">${([["code", "Code"], ["day", "Today's move"], ["since", "Since added"], ["health", "Health ★"], ["added", "Newest added"], ["next", "Next date"]] as [Sort, string][]).map(([k, l]) => `<option value="${k}"${sort === k ? " selected" : ""}>${l}</option>`).join("")}</select></label>
      <span class="muted small">${shown.length} stock${shown.length === 1 ? "" : "s"}. Add stocks with ☆ on any stock page, or Watch on a lead.</span></div>
    <ul class="wl-items"${chosen === "black" ? " hidden" : ""}>${shown.map((s) => row(s, byId, ratings)).join("") || `<li class="muted">${data.stocks.length ? "No stocks in this list yet: tap Edit on a stock to add it." : "Nothing watched yet. Open a stock and tap ☆."}</li>`}</ul>`;

  // Prices load per stock (from Gold's cache), then the list re-sorts if sorting by a price figure.
  const figures = new Map<string, { day: number | null; since: number | null }>();
  const sortRows = () => {
    const ul = el.querySelector<HTMLElement>(".wl-items")!;
    const items = [...ul.querySelectorAll<HTMLElement>("li[data-code]")];
    const val = (code: string): number | string | null => {
      const s = data.stocks.find((x) => x.code === code)!;
      if (sort === "code") return code;
      if (sort === "day") return figures.get(code)?.day ?? null;
      if (sort === "since") return figures.get(code)?.since ?? null;
      if (sort === "health") return ratings[code]?.health ?? null;
      if (sort === "added") return -Date.parse(s.addedAt);
      return s.next?.date ?? null;
    };
    items.sort((a, b) => {
      const x = val(a.dataset.code!), y = val(b.dataset.code!);
      if (x == null) return y == null ? 0 : 1;
      if (y == null) return -1;
      if (typeof x === "string") return sort === "next" || sort === "code" ? x.localeCompare(String(y)) : 0;
      return sort === "added" ? (x as number) - (y as number) : (y as number) - (x as number);
    }).forEach((li) => ul.appendChild(li));
  };
  for (const s of shown) {
    loader(s.code).then((r) => {
      const li = el.querySelector<HTMLElement>(`li[data-code="${s.code}"]`);
      const p = r.data;
      if (!li || !p) return;
      const last = p.price ?? p.bars?.at(-1)?.c ?? null;
      const prev = p.previousClose ?? p.bars?.at(-2)?.c ?? null;
      const day = last && prev ? last / prev - 1 : null;
      const since = last && s.priceAdded ? last / s.priceAdded - 1 : null;
      figures.set(s.code, { day, since });
      li.querySelector(".wl-name")!.textContent = p.name ?? "";
      li.querySelector(".wl-px")!.innerHTML = `${price(last)} <small class="${tone(day)}">${pct(day, true)}</small>`;
      li.querySelector(".wl-since")!.innerHTML = since != null ? `<span class="${tone(since)}">${pct(since, true)}</span> since added` : `<span class="muted">added ${date(s.addedAt)}</span>`;
      if (sort === "day" || sort === "since") sortRows();
    }).catch(() => {});
  }
  sortRows();

  // Events
  el.querySelectorAll<HTMLButtonElement>(".bl-edit").forEach((b) => b.addEventListener("click", () => {
    openBlacklist(b.closest<HTMLElement>("[data-bl]")!.dataset.bl!, () => renderWatchlists(app, ""));
  }));
  el.querySelectorAll<HTMLButtonElement>("[data-list]").forEach((b) => b.addEventListener("click", () => {
    chosen = b.dataset.list === "all" || b.dataset.list === "none" || b.dataset.list === "black" ? (b.dataset.list as "all" | "none" | "black") : Number(b.dataset.list);
    renderWatchlists(app);
  }));
  el.querySelector("#new-list")!.addEventListener("click", async () => {
    const name = prompt("Name for the new list (e.g. Lithium, Speculative, Dividends):");
    if (!name?.trim()) return;
    const d = await api<typeof data>("watchlists", "POST", { name });
    chosen = d.lists.find((l) => l.name.toLowerCase() === name.trim().toLowerCase())?.id ?? "all";
    renderWatchlists(app);
  });
  el.querySelector<HTMLSelectElement>("#wl-sort")!.addEventListener("change", (e) => { sort = (e.target as HTMLSelectElement).value as Sort; sortRows(); });
  if (list) {
    el.querySelector("#rename-list")!.addEventListener("click", async () => {
      const name = prompt("New name:", list.name);
      if (name?.trim()) { await api(`watchlists/${list.id}`, "PATCH", { name }).catch((e) => alert(e.message)); renderWatchlists(app); }
    });
    el.querySelector("#colour-list")!.addEventListener("click", async () => {
      const next = COLORS[(COLORS.indexOf(list.color ?? "a0") + 1) % COLORS.length];
      await api(`watchlists/${list.id}`, "PATCH", { color: next });
      renderWatchlists(app);
    });
    el.querySelector("#delete-list")!.addEventListener("click", async () => {
      if (!confirm(`Delete the "${list.name}" list? Its stocks stay on your watchlist.`)) return;
      await api(`watchlists/${list.id}`, "DELETE");
      chosen = "all";
      renderWatchlists(app);
    });
    el.querySelector("#review-list")?.addEventListener("click", async (e) => {
      const b = e.currentTarget as HTMLButtonElement;
      const text = listReviewPrompt(list.name, shown.map((s) => s.code));
      try { await navigator.clipboard.writeText(text); b.textContent = "Copied: paste it into the Claude desktop app"; } catch { prompt("Copy this into the Claude desktop app:", text); }
    });
  }
  el.querySelectorAll<HTMLButtonElement>("[data-edit]").forEach((b) => b.addEventListener("click", () => {
    editing = editing === b.dataset.edit ? null : b.dataset.edit!;
    renderWatchlists(app);
  }));
  el.querySelectorAll<HTMLFormElement>("form.wl-edit").forEach((f) => f.addEventListener("submit", async (e) => {
    e.preventDefault();
    const code = f.dataset.code!;
    const fd = new FormData(f);
    await api(`watchlist/${code}`, "PATCH", { note: fd.get("note"), lists: fd.getAll("list").map(Number) }).catch((err) => alert(err.message));
    editing = null;
    renderWatchlists(app);
  }));
  el.querySelectorAll<HTMLButtonElement>("[data-unwatch]").forEach((b) => b.addEventListener("click", async () => {
    if (!confirm(`Stop watching ${b.dataset.unwatch}?`)) return;
    await api(`watchlist/${b.dataset.unwatch}`, "DELETE");
    editing = null;
    renderWatchlists(app);
  }));
  if (editing) el.querySelector(`li[data-code="${editing}"]`)?.scrollIntoView({ block: "center" });
}

function row(s: Watched, lists: Map<number, List>, ratings: Record<string, { health: number }>) {
  const open = editing === s.code;
  return `<li class="card wl-item" data-code="${esc(s.code)}">
    <div class="wl-top">
      <span>${stockLink(s.code)}${ratings[s.code] ? ` <a href="#/${esc(s.code)}/assess" class="wl-stars">${starsHtml(ratings[s.code].health)}</a>` : ""} <span class="muted small wl-name"></span></span>
      <span class="wl-px muted small">…</span>
    </div>
    <div class="wl-meta small">
      <span class="wl-since"></span>
      ${s.next ? `<a href="#/${esc(s.code)}/calendar" class="wl-next">📅 ${date(s.next.date)}: ${esc(s.next.title.slice(0, 60))}</a>` : ""}
    </div>
    <div class="chips">${s.lists.map((id) => lists.get(id)).filter(Boolean).map((l) => `<span class="chip"><i class="key ${esc(l!.color ?? "a0")}"></i>${esc(l!.name)}</span>`).join("")}
      <button class="small-btn" data-edit="${esc(s.code)}">${open ? "Close" : "Edit"}</button></div>
    ${s.note && !open ? `<p class="muted small">${esc(s.note)}</p>` : ""}
    ${open ? `<form class="wl-edit" data-code="${esc(s.code)}">
      <div class="checks">${[...lists.values()].map((l) => `<label><input type="checkbox" name="list" value="${l.id}"${s.lists.includes(l.id) ? " checked" : ""}> ${esc(l.name)}</label>`).join("") || `<span class="muted small">No lists yet: make one with “+ New list”.</span>`}</div>
      <textarea name="note" rows="2" maxlength="500" placeholder="Why you're watching it">${esc(s.note ?? "")}</textarea>
      <div class="actions"><button class="primary small-btn">Save</button><button type="button" class="link-btn" data-unwatch="${esc(s.code)}">Stop watching</button></div>
    </form>` : ""}
  </li>`;
}

function blacklistHtml() {
  const b = Object.entries(getMarks().blacklisted).sort(([x], [y]) => x.localeCompare(y));
  return `<section class="card bl-list"><h3>Blacklist</h3>
    ${b.length ? `<ul class="plain">${b.map(([code, v]) => `<li data-bl="${esc(code)}">${stockLink(code)} <span>${v.reason ? esc(v.reason) : `<span class="muted">No reason given</span>`}</span>
      <span class="muted small">since ${date(v.addedAt)}</span> <button class="small-btn bl-edit">View / remove</button></li>`).join("")}</ul>`
      : `<p class="muted">Nothing blacklisted. On a stock's page, tap ⊘ to blacklist it with a reason: it'll still show everywhere, greyed and marked ⊘.</p>`}</section>`;
}
